import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PublishIcon from '@mui/icons-material/Publish';
import ReplayIcon from '@mui/icons-material/Replay';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RestoreIcon from '@mui/icons-material/Restore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  AiImageErrorDiagnostic,
  AiImageEvent,
  AiImageGeneration,
  AiImageOutput,
} from '../types';
import { downloadAiImageOutput, getAiImageAssetUrl, getAiImageOutputPreviewUrl } from '../utils';
import { formatImagePixelLabel, getAiImageModelLabel } from '../modelCatalog';
import { describeActualImageCost } from '../costEstimate';
import { getAiImageStatusColor, getAiImageStatusLabel } from '../studioPresentation';
import StudioActionDialog from './StudioActionDialog';

interface GenerationDetailsDrawerProps {
  generation: AiImageGeneration | null;
  selectedOutput: AiImageOutput | null;
  open: boolean;
  actionLoading: string | null;
  canReview: boolean;
  canPublish: boolean;
  onClose: () => void;
  onApproveRequest: (generationId: string, outputId: string) => void;
  onReject: (generationId: string, outputId: string, reason?: string) => Promise<void>;
  onPublishRequest: (generationId: string, outputId: string) => void;
  onReuse: (generationId: string, outputId: string) => Promise<void>;
  onUpscale?: (generationId: string, outputId: string) => Promise<void>;
  onEdit?: (generation: AiImageGeneration, output: AiImageOutput) => void;
  onUsePrompt: () => void;
  onReuseAll: () => void;
  onOpenRecipe?: () => void;
  onAddComment?: (generationId: string, outputId: string, text: string) => Promise<void>;
  onMarkRead?: (generationId: string, outputId: string) => Promise<void>;
  currentUserId?: string;
}

const drawerWidth = 380;

function ActualCostDetails({ generation }: { generation: AiImageGeneration }) {
  const described = describeActualImageCost(generation.cost, generation.usdSekRate);
  if (!described) return null;
  return (
    <>
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', mb: 0.5, display: 'block' }}>
          API-kostnad
        </Typography>
        <List dense disablePadding>
          <ListItem disableGutters sx={{ py: 0.25 }}>
            <ListItemText
              primary={described.summary}
              primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8rem', fontWeight: 600 } }}
            />
          </ListItem>
          {described.lines.map((line) => (
            <ListItem key={line.label} disableGutters sx={{ py: 0.25 }}>
              <ListItemText
                primary={line.label}
                secondary={line.value}
                primaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.72rem', color: 'text.secondary' } }}
                secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8rem', fontWeight: 500 } }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
      <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
    </>
  );
}

function isAiImageErrorDiagnostic(value: unknown): value is AiImageErrorDiagnostic {
  return Boolean(
    value
    && typeof value === 'object'
    && typeof (value as AiImageErrorDiagnostic).code === 'string'
    && typeof (value as AiImageErrorDiagnostic).userMessage === 'string'
  );
}

function getEventDiagnostics(event: AiImageEvent): AiImageErrorDiagnostic[] {
  const diagnostics: AiImageErrorDiagnostic[] = [];
  const details = event.details;
  if (!details) {
    return diagnostics;
  }
  if (isAiImageErrorDiagnostic(details.diagnostic)) {
    diagnostics.push(details.diagnostic);
  }
  if (Array.isArray(details.diagnostics)) {
    for (const diagnostic of details.diagnostics) {
      if (isAiImageErrorDiagnostic(diagnostic)) {
        diagnostics.push(diagnostic);
      }
    }
  }
  return diagnostics;
}

function getDiagnosticMetadata(diagnostic: AiImageErrorDiagnostic): Array<{ label: string; value: string }> {
  const details = diagnostic.details || {};
  const values = [
    diagnostic.stage ? { label: 'Steg', value: diagnostic.stage } : null,
    diagnostic.code ? { label: 'Kod', value: diagnostic.code } : null,
    diagnostic.source ? { label: 'Källa', value: diagnostic.source } : null,
    typeof diagnostic.upstreamStatus === 'number' ? { label: 'HTTP', value: String(diagnostic.upstreamStatus) } : null,
    { label: 'Retrybart', value: diagnostic.retryable ? 'Ja' : 'Nej' },
    typeof details.outputIndex === 'number' ? { label: 'Build', value: String(details.outputIndex) } : null,
    typeof details.referenceIndex === 'number' ? { label: 'Referens', value: String(details.referenceIndex) } : null,
    typeof details.referenceType === 'string' ? { label: 'Typ', value: details.referenceType } : null,
    typeof details.model === 'string' ? { label: 'Modell', value: details.model } : null,
  ];

  return values.filter((value): value is { label: string; value: string } => Boolean(value));
}

const GenerationDetailsDrawer: React.FC<GenerationDetailsDrawerProps> = ({
  generation,
  selectedOutput,
  open,
  actionLoading,
  canReview,
  canPublish,
  onClose,
  onApproveRequest,
  onReject,
  onPublishRequest,
  onReuse,
  onUpscale,
  onEdit,
  onUsePrompt,
  onReuseAll,
  onOpenRecipe,
  onAddComment,
  onMarkRead,
  currentUserId,
}) => {
  const [commentText, setCommentText] = useState('');
  const [commentSending, setCommentSending] = useState(false);
  const [referencePreviewUrl, setReferencePreviewUrl] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSendComment = async () => {
    if (!commentText.trim() || !generation || !selectedOutput || !onAddComment) return;
    setCommentSending(true);
    try {
      await onAddComment(generation._id, selectedOutput._id, commentText.trim());
      setCommentText('');
    } finally {
      setCommentSending(false);
    }
  };

  const fullImageUrl = selectedOutput
    ? (getAiImageAssetUrl(selectedOutput.publicUrl) || getAiImageOutputPreviewUrl(selectedOutput))
    : '';

  const handleDownload = async () => {
    if (!selectedOutput) return;
    try {
      await downloadAiImageOutput(selectedOutput);
      setSnackbar({ open: true, message: 'Bilden laddades ned.', severity: 'success' });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'Kunde inte ladda ned bilden.',
        severity: 'error',
      });
    }
  };

  const diagnosticEvents = generation
    ? generation.events
      .map((event) => ({
        event,
        diagnostics: getEventDiagnostics(event),
      }))
      .filter(({ event, diagnostics }) => diagnostics.length > 0 || typeof event.details?.error === 'string')
    : [];
  const archiveKeywords = selectedOutput?.archiveKeywords?.filter((keyword) => keyword.trim()) ?? [];
  const hasArchiveMetadata = Boolean(
    selectedOutput?.archiveDescription?.trim()
    || archiveKeywords.length > 0
    || selectedOutput?.archiveCategory
  );

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: `calc(${drawerWidth}px + 50vw)`, lg: `calc(${drawerWidth}px + 55vw)` },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'row',
          bgcolor: '#0a0a0a',
          overflow: 'hidden',
        },
      }}
    >
      {/* --- Image preview area --- */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          position: 'relative',
        }}
      >
        {fullImageUrl ? (
          <Box
            component="img"
            src={fullImageUrl}
            alt={generation?.prompt || 'Genererad bild'}
            sx={{
              maxWidth: '100%',
              maxHeight: '92vh',
              objectFit: 'contain',
              borderRadius: 1.5,
              filter: 'drop-shadow(0 8px 32px rgba(0,0,0,0.4))',
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>
            Ingen bild vald
          </Typography>
        )}

        {/* Floating action bar on image */}
        {fullImageUrl && selectedOutput && generation && (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              position: 'absolute',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(12px)',
              borderRadius: 3,
              px: 2,
              py: 0.75,
            }}
          >
            <Tooltip title="Ladda ned">
              <IconButton
                aria-label="Ladda ned"
                size="small"
                sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                onClick={() => void handleDownload()}
              >
                <DownloadIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Använd som referens">
              <IconButton
                size="small"
                sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                onClick={() => void onReuse(generation._id, selectedOutput._id)}
              >
                <ReplayIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            {onEdit && (
              <Tooltip title="Redigera">
                <IconButton
                  size="small"
                  sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                  onClick={() => onEdit(generation, selectedOutput)}
                >
                  <AutoFixHighIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Använd prompt">
              <IconButton
                size="small"
                sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                onClick={onUsePrompt}
              >
                <TextSnippetIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            {onUpscale && (
              <Tooltip title="Skala upp till 4K">
                <IconButton
                  size="small"
                  sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                  onClick={() => void onUpscale(generation._id, selectedOutput._id)}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Återanvänd allt (prompt + referenser)">
              <IconButton
                size="small"
                sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                onClick={onReuseAll}
              >
                <RestoreIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>

      {/* --- Detail sidebar --- */}
      <Box
        sx={{
          width: { xs: '100%', md: drawerWidth },
          flexShrink: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          borderLeft: { md: '1px solid' },
          borderColor: { md: 'rgba(0,0,0,0.06)' },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'rgba(0,0,0,0.06)',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            Bilddetaljer
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {!generation || !selectedOutput ? (
          <Box sx={{ p: 3, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.disabled">
              Välj en bild för att se detaljer.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Mobile image preview */}
            <Box
              component="img"
              src={fullImageUrl}
              alt={generation.prompt || 'Genererad bild'}
              sx={{
                width: '100%',
                maxHeight: '55vh',
                minHeight: 220,
                objectFit: 'contain',
                borderRadius: 2,
                bgcolor: 'grey.100',
                display: { xs: 'block', md: 'none' },
              }}
            />

            {/* Status chips */}
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }} useFlexGap>
              <Chip
                size="small"
                label={getAiImageStatusLabel(selectedOutput.status)}
                color={getAiImageStatusColor(selectedOutput.status)}
                variant={selectedOutput.status === 'completed' ? 'outlined' : 'filled'}
              />
              <Chip size="small" variant="outlined" label={getAiImageModelLabel(generation.model)} sx={{ fontSize: '0.7rem' }} />
              {selectedOutput.publishedAssetId && (
                <Chip size="small" color="success" label="Publicerad" />
              )}
            </Stack>

            {/* Action buttons */}
            <Stack spacing={1}>
              {canPublish && selectedOutput.status === 'completed' && (
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  size="medium"
                  startIcon={<CheckCircleOutlineIcon />}
                  disabled={actionLoading === `approve:${selectedOutput._id}`}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2.5,
                    fontWeight: 600,
                    py: 1,
                    boxShadow: '0 2px 8px rgba(46,125,50,0.3)',
                  }}
                  onClick={() => onApproveRequest(generation._id, selectedOutput._id)}
                >
                  Godkänn och publicera
                </Button>
              )}

              {canPublish && selectedOutput.status === 'approved' && (
                <Button
                  fullWidth
                  variant="contained"
                  size="medium"
                  startIcon={<PublishIcon />}
                  disabled={actionLoading === `publish:${selectedOutput._id}`}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2.5,
                    fontWeight: 600,
                    py: 1,
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)',
                    boxShadow: '0 2px 8px rgba(15,52,96,0.3)',
                  }}
                  onClick={() => onPublishRequest(generation._id, selectedOutput._id)}
                >
                  Publicera till DAM
                </Button>
              )}

              <Stack direction="row" spacing={0.75}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<ReplayIcon />}
                  sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', borderColor: 'rgba(0,0,0,0.12)' }}
                  onClick={() => onReuse(generation._id, selectedOutput._id)}
                >
                  Referens
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<TextSnippetIcon />}
                  sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', borderColor: 'rgba(0,0,0,0.12)' }}
                  onClick={onUsePrompt}
                >
                  Prompt
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', borderColor: 'rgba(0,0,0,0.12)' }}
                  onClick={() => void handleDownload()}
                >
                  Hämta
                </Button>
              </Stack>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<RestoreIcon />}
                sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', borderColor: 'rgba(0,0,0,0.12)' }}
                onClick={onReuseAll}
              >
                Återanvänd allt (prompt + referenser)
              </Button>
              {onUpscale && (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<AutoAwesomeIcon />}
                  sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', borderColor: 'rgba(0,0,0,0.12)' }}
                  onClick={() => onUpscale(generation._id, selectedOutput._id)}
                >
                  Skala upp till 4K
                </Button>
              )}
              {generation.workflow === 'recipe_refresh' && generation.targetAssetId && onOpenRecipe && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<RestoreIcon />}
                  sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', borderColor: 'rgba(0,0,0,0.12)' }}
                  onClick={onOpenRecipe}
                >
                  Visa recept
                </Button>
              )}

              {canReview && selectedOutput.status !== 'published_to_dam' && selectedOutput.status !== 'rejected' && (
                <Button
                  fullWidth
                  variant="text"
                  color="error"
                  size="small"
                  startIcon={<HighlightOffIcon />}
                  disabled={actionLoading === `reject:${selectedOutput._id}`}
                  sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem' }}
                  onClick={() => setRejectOpen(true)}
                >
                  Avslå
                </Button>
              )}
            </Stack>

            <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

            {/* Prompt */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', mb: 0.5, display: 'block' }}>
                Prompt
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.82rem', lineHeight: 1.5, color: 'text.primary' }}>
                {generation.prompt || 'Ingen prompt sparad.'}
              </Typography>
            </Box>

            {/* Metadata grid */}
            {generation.workflow === 'recipe_refresh' && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Den här bilden är skapad för att förnya en receptbild
                {generation.targetAssetId ? ` (asset ${generation.targetAssetId}).` : '.'}
              </Alert>
            )}

            {/* Metadata grid */}
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'grey.50',
              }}
            >
              {[
                { label: 'Skapad av', value: generation.userName || '-' },
                { label: 'Skapad', value: new Date(generation.createdAt).toLocaleString('sv-SE') },
                { label: 'Modell', value: getAiImageModelLabel(generation.model) },
                { label: 'Ratio', value: generation.aspectRatio },
                { label: 'Begärd storlek', value: generation.imageSize },
                { label: 'Faktiska pixlar', value: formatImagePixelLabel(selectedOutput.width, selectedOutput.height) || '-' },
                { label: 'Antal', value: String(generation.outputCount) },
                { label: 'Filstorlek', value: `${Math.max(1, Math.round(selectedOutput.fileSize / 1024))} KB` },
              ].map((item) => (
                <Box key={item.label}>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* References */}
            {generation.references.length > 0 && (
              <>
                <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', mb: 0.75, display: 'block' }}>
                    Referenser
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
                    {generation.references.map((reference) => {
                      const refSrc = getAiImageAssetUrl(reference.sourceUrl || reference.thumbnailUrl);
                      return (
                        <Box
                          key={reference.referenceId}
                          sx={{
                            minWidth: 80,
                            maxWidth: 100,
                            borderRadius: 1.5,
                            bgcolor: 'grey.50',
                            p: 0.5,
                            flexShrink: 0,
                            border: '1px solid',
                            borderColor: 'rgba(0,0,0,0.04)',
                            cursor: 'pointer',
                            transition: 'border-color 0.15s, box-shadow 0.15s',
                            '&:hover': { borderColor: 'primary.main', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                          }}
                          onClick={() => setReferencePreviewUrl(refSrc || null)}
                        >
                          <Avatar
                            variant="rounded"
                            src={getAiImageAssetUrl(reference.thumbnailUrl || reference.sourceUrl)}
                            sx={{ width: '100%', height: 52, bgcolor: 'grey.200', mb: 0.5, borderRadius: 1 }}
                          />
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, fontSize: '0.75rem' }} noWrap>
                            {reference.title || reference.fileName || reference.sku || 'Referens'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </>
            )}

            <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

            {hasArchiveMetadata && (
              <>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', mb: 0.5, display: 'block' }}>
                    Arkivmetadata
                  </Typography>
                  {selectedOutput.archiveDescription && (
                    <Box sx={{ mb: selectedOutput.archiveCategory || archiveKeywords.length > 0 ? 1 : 0 }}>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 0.25 }}>
                        Beskrivning
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.82rem', lineHeight: 1.5, color: 'text.primary' }}>
                        {selectedOutput.archiveDescription}
                      </Typography>
                    </Box>
                  )}
                  {(selectedOutput.archiveCategory || archiveKeywords.length > 0) && (
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                      {selectedOutput.archiveCategory && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Kategori: ${selectedOutput.archiveCategory}`}
                          sx={{ fontSize: '0.75rem', height: 24 }}
                        />
                      )}
                      {archiveKeywords.map((keyword) => (
                        <Chip
                          key={keyword}
                          size="small"
                          variant="outlined"
                          label={keyword}
                          sx={{ fontSize: '0.75rem', height: 24 }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>

                <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
              </>
            )}

            <ActualCostDetails generation={generation} />

            {/* Status section */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', mb: 0.5, display: 'block' }}>
                Status
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="Generationsstatus"
                    secondary={generation.status}
                    primaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.72rem', color: 'text.secondary' } }}
                    secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8rem', fontWeight: 500 } }}
                  />
                </ListItem>
                <ListItem disableGutters sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="Bildstatus"
                    secondary={selectedOutput.status}
                    primaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.72rem', color: 'text.secondary' } }}
                    secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8rem', fontWeight: 500 } }}
                  />
                </ListItem>
                {selectedOutput.publishedAssetId && (
                  <ListItem disableGutters sx={{ py: 0.25 }}>
                    <ListItemText
                      primary="DAM-tillgång"
                      secondary={selectedOutput.publishedAssetId}
                      primaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.72rem', color: 'text.secondary' } }}
                      secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8rem', fontWeight: 500 } }}
                    />
                  </ListItem>
                )}
                {selectedOutput.rejectedReason && (
                  <ListItem disableGutters sx={{ py: 0.25 }}>
                    <ListItemText
                      primary="Avslagsorsak"
                      secondary={selectedOutput.rejectedReason}
                      primaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.72rem', color: 'text.secondary' } }}
                      secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8rem', fontWeight: 500, color: 'error.main' } }}
                    />
                  </ListItem>
                )}
                {selectedOutput.publishError && (
                  <ListItem disableGutters sx={{ py: 0.25 }}>
                    <ListItemText
                      primary="Publiceringsfel"
                      secondary={selectedOutput.publishError}
                      primaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.72rem', color: 'text.secondary' } }}
                      secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8rem', fontWeight: 500, color: 'error.main' } }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>

            {(generation.errorMessage || diagnosticEvents.length > 0) && (
              <>
                <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', mb: 0.75, display: 'block' }}>
                    Feldiagnostik
                  </Typography>
                  {generation.errorMessage && (
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'error.main', mb: diagnosticEvents.length > 0 ? 1 : 0 }}>
                      {generation.errorMessage}
                    </Typography>
                  )}
                  {diagnosticEvents.length > 0 && (
                    <Stack spacing={1}>
                      {diagnosticEvents.map(({ event, diagnostics }, index) => (
                        <Box
                          key={`${event.type}-${event.at}-${index}`}
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            bgcolor: 'rgba(211,47,47,0.04)',
                            border: '1px solid',
                            borderColor: 'rgba(211,47,47,0.16)',
                          }}
                        >
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: '0.75rem', mb: 0.35 }}>
                            {event.message || event.type}
                          </Typography>
                          {diagnostics.length > 0 ? diagnostics.map((diagnostic, diagnosticIndex) => (
                            <Box key={`${diagnostic.code}-${diagnosticIndex}`} sx={{ '&:not(:last-child)': { mb: 0.9 } }}>
                              <Typography variant="body2" sx={{ fontSize: '0.79rem', fontWeight: 500 }}>
                                {diagnostic.userMessage}
                              </Typography>
                              {diagnostic.technicalMessage && diagnostic.technicalMessage !== diagnostic.userMessage && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary', fontSize: '0.75rem' }}>
                                  {diagnostic.technicalMessage}
                                </Typography>
                              )}
                              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.75 }}>
                                {getDiagnosticMetadata(diagnostic).map((item) => (
                                  <Chip
                                    key={`${diagnostic.code}-${item.label}`}
                                    size="small"
                                    variant="outlined"
                                    label={`${item.label}: ${item.value}`}
                                    sx={{ fontSize: '0.75rem', height: 22 }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          )) : (
                            <Typography variant="body2" sx={{ fontSize: '0.79rem', fontWeight: 500 }}>
                              {event.details?.error}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </>
            )}

            {/* Comments */}
            <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', mb: 0.75, display: 'block' }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 13, mr: 0.5, verticalAlign: 'text-bottom' }} />
                Kommentarer {selectedOutput.comments && selectedOutput.comments.length > 0 ? `(${selectedOutput.comments.length})` : ''}
              </Typography>

              {selectedOutput.comments && selectedOutput.comments.length > 0 && (
                <Stack spacing={1} sx={{ mb: 1.5 }}>
                  {selectedOutput.comments.map((c) => (
                    <Box key={c._id} sx={{ p: 1.25, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'rgba(0,0,0,0.04)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
                          {c.userName || 'Användare'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                          {new Date(c.createdAt).toLocaleString('sv-SE')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                        {c.text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}

              {onMarkRead && selectedOutput.comments && selectedOutput.comments.length > 0 && (() => {
                const allRead = currentUserId
                  ? selectedOutput.comments!.every((c) => c.readBy?.includes(currentUserId!))
                  : false;

                return allRead ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5, px: 1, borderRadius: 2, bgcolor: 'rgba(46,125,50,0.08)' }}>
                    <DoneAllIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, fontSize: '0.75rem' }}>
                      Alla kommentarer markerade som lästa
                    </Typography>
                  </Box>
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
                    onClick={() => void onMarkRead(generation._id, selectedOutput._id)}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      alignSelf: 'flex-start',
                      borderRadius: 2,
                      boxShadow: 'none',
                      '&:hover': { boxShadow: '0 2px 6px rgba(25,118,210,0.3)' },
                    }}
                  >
                    Markera alla som lästa
                  </Button>
                );
              })()}

              {onAddComment && (
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={3}
                    placeholder="Skriv en kommentar..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void handleSendComment();
                      }
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' } }}
                  />
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={!commentText.trim() || commentSending}
                    onClick={() => void handleSendComment()}
                    sx={{
                      bgcolor: 'primary.main',
                      color: '#fff',
                      width: 34,
                      height: 34,
                      flexShrink: 0,
                      '&:hover': { bgcolor: 'primary.dark' },
                      '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.400' },
                    }}
                  >
                    <SendIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
            </Box>

            {/* Events */}
            {generation.events.length > 0 && (
              <>
                <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', mb: 0.5, display: 'block' }}>
                    Händelser
                  </Typography>
                  <List dense disablePadding>
                    {generation.events.map((event, index) => (
                      <ListItem key={`${event.type}-${event.at}-${index}`} disableGutters sx={{ py: 0.15 }}>
                        <ListItemText
                          primary={event.message || event.type}
                          secondary={new Date(event.at).toLocaleString('sv-SE')}
                          primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.78rem' } }}
                          secondaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.75rem' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </Drawer>

    <Dialog
      open={Boolean(referencePreviewUrl)}
      onClose={() => setReferencePreviewUrl(null)}
      maxWidth={false}
      sx={{
        zIndex: 20000,
        '& .MuiBackdrop-root': { bgcolor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' },
      }}
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          boxShadow: 'none',
          borderRadius: 3,
          overflow: 'visible',
          maxWidth: '90vw',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'visible' }}>
        <IconButton
          onClick={() => setReferencePreviewUrl(null)}
          sx={{
            position: 'absolute',
            top: -12,
            right: -12,
            color: '#fff',
            zIndex: 10,
            bgcolor: 'rgba(255,255,255,0.12)',
            backdropFilter: 'blur(8px)',
            width: 36,
            height: 36,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
        {referencePreviewUrl && (
          <Box
            component="img"
            src={referencePreviewUrl}
            alt="Referensbild"
            sx={{ maxWidth: '85vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 2, display: 'block' }}
          />
        )}
      </DialogContent>
    </Dialog>
    <StudioActionDialog
      open={rejectOpen}
      title="Avslå bild"
      description="Avslaget sparas på den valda bilden. En orsak är valfri."
      confirmLabel="Avslå"
      confirmColor="error"
      textLabel="Orsak"
      textPlaceholder="Valfritt"
      onClose={() => setRejectOpen(false)}
      onConfirm={(reason) => {
        if (!generation || !selectedOutput) return;
        setRejectOpen(false);
        void onReject(generation._id, selectedOutput._id, reason);
      }}
    />
    <Snackbar
      open={snackbar.open}
      autoHideDuration={4000}
      onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled">
        {snackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
};

export default GenerationDetailsDrawer;
